export interface PendingAutoPf {
    year?: number;
    month?: number;
    provident_fund_type?: "mandatory" | "voluntary";

    employee_share_percent?: number;
    organization_share_percent?: number;

    selected_earning_ids?: number[];
    description?: string;
    status?: string;
}

export interface PendingDeleteAutoPf {
    awaitingInfo?: boolean;
}

export const startCreateAutoPfFlow = (
    setPending: (data: PendingAutoPf | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPending({});
    pushAIChat("Sure! For which year should I process PF contribution?");
    speak("For which year should I process PF contribution?");
};

export const processPendingAutoPf = (
    text: string,
    pending: PendingAutoPf,
    setPending: (data: PendingAutoPf | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — YEAR
    if (!pending.year) {
        const year = Number(text);
        if (isNaN(year) || year < 2000) {
            pushAIChat("Please provide a valid year.");
            speak("Please provide a valid year.");
            return;
        }

        setPending({ ...pending, year });
        pushAIChat("Which month should this contribution be for?");
        speak("Which month should this contribution be for?");
        return;
    }

    // STEP 2 — MONTH
    if (!pending.month) {
        const month = Number(text);
        if (isNaN(month) || month < 1 || month > 12) {
            pushAIChat("Please provide a valid month (1 to 12).");
            speak("Please provide a valid month.");
            return;
        }

        setPending({ ...pending, month });
        pushAIChat("Is this Mandatory or Voluntary Provident Fund?");
        speak("Is this mandatory or voluntary provident fund?");
        return;
    }

    // STEP 3 — PF TYPE
    if (!pending.provident_fund_type) {
        const type =
            text.toLowerCase().includes("voluntary")
                ? "voluntary"
                : "mandatory";

        setPending({ ...pending, provident_fund_type: type });

        pushAIChat("What is the employee contribution percentage?");
        speak("What is the employee contribution percentage?");
        return;
    }

    // STEP 4 — EMPLOYEE %
    if (pending.employee_share_percent === undefined) {
        const pct = Number(text);
        if (isNaN(pct) || pct < 0) {
            pushAIChat("Please provide a valid percentage.");
            speak("Please provide a valid percentage.");
            return;
        }

        setPending({ ...pending, employee_share_percent: pct });
        pushAIChat("What is the organization contribution percentage?");
        speak("What is the organization contribution percentage?");
        return;
    }

    // STEP 5 — ORG %
    if (pending.organization_share_percent === undefined) {
        const pct = Number(text);
        if (isNaN(pct) || pct < 0) {
            pushAIChat("Please provide a valid percentage.");
            speak("Please provide a valid percentage.");
            return;
        }

        setPending({ ...pending, organization_share_percent: pct });

        pushAIChat("Please select earning heads to include.");
        speak("Please select earning heads.");

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "earning-head-multi" }
            })
        );
        return;
    }

    // STEP 6 — EARNING HEADS
    if (pending.selected_earning_ids && pending.selected_earning_ids.length > 0) {
        const finalPayload = {
            ...pending,
            description: `Auto PF for ${pending.month}-${pending.year}`,
            status: "active"
        };

        pushAIChat("Processing provident fund contribution automatically...");
        speak("Processing provident fund contribution.");

        window.dispatchEvent(
            new CustomEvent("pf-auto-process", {
                detail: finalPayload
            })
        );

        setPending(null);
        return;
    }

    // FINAL SUBMIT (AUTO PROCESS)
    const finalPayload = {
        ...pending,
        description: `Auto PF for ${pending.month}-${pending.year}`,
        status: "active"
    };

    pushAIChat("Processing provident fund contribution automatically...");
    speak("Processing provident fund contribution.");

    window.dispatchEvent(
        new CustomEvent("pf-auto-process", {
            detail: finalPayload
        })
    );

    setPending(null);
};

export const startDeleteAutoPfFlow = (
    setPendingDelete: (data: PendingDeleteAutoPf | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPendingDelete({ awaitingInfo: true });
    pushAIChat("Please provide month and year to delete PF contribution.");
    speak("Please provide month and year.");
};

export const processPendingDeleteAutoPf = (
    text: string,
    pending: PendingDeleteAutoPf,
    setPendingDelete: (data: PendingDeleteAutoPf | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingInfo) return;

    // Expected: "March 2025"
    const parts = text.trim().split(" ");

    if (parts.length < 2) {
        pushAIChat("Please provide month and year.");
        speak("Please provide month and year.");
        return;
    }

    const year = Number(parts.pop());
    const monthText = parts.join(" ");

    if (isNaN(year)) {
        pushAIChat("Invalid year.");
        speak("Invalid year.");
        return;
    }

    const month = new Date(`${monthText} 1`).getMonth() + 1;

    if (!month || month < 1 || month > 12) {
        pushAIChat("Invalid month.");
        speak("Invalid month.");
        return;
    }

    pushAIChat(`Deleting PF contribution for ${month}-${year}...`);
    speak(`Deleting PF contribution for ${month}-${year}.`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-auto-pf-process", {
            detail: { year, month }
        })
    );

    setPendingDelete(null);
};
